import { createEffect, createSignal, on, onCleanup, untrack, type Accessor } from "solid-js"

/**
 * `source`, except that a value going away stays on screen for `holdMs` first.
 *
 * For a line that names the current one of a stream of short-lived things. A
 * turn's tool calls are written, run and end one after another, and the gap
 * between two of them is empty for a few hundred milliseconds each time: a
 * line that vanishes and comes back in every gap blinks, and in the plan pill
 * — which is as wide as its text — the whole pill jumped with it («эта хуйня
 * постоянно мигает», 30.09). A new value replaces the held one at once, and
 * `active` going false clears it at once: a finished turn must not keep
 * describing work.
 */
export function createHeldValue<T>(
  source: Accessor<T | undefined>,
  holdMs: number,
  active: Accessor<boolean> = () => true,
): Accessor<T | undefined> {
  const [held, setHeld] = createSignal<T | undefined>(untrack(() => (active() ? source() : undefined)))
  let timer: ReturnType<typeof setTimeout> | undefined
  const stop = () => {
    if (timer === undefined) return
    clearTimeout(timer)
    timer = undefined
  }
  createEffect(
    on([source, active], ([value, live]) => {
      stop()
      if (!live) return setHeld(undefined)
      if (value !== undefined) return setHeld(() => value)
      timer = setTimeout(() => {
        timer = undefined
        setHeld(undefined)
      }, holdMs)
    }),
  )
  onCleanup(stop)
  return held
}
